import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, MailPlusIcon, DownloadIcon, ArrowRightIcon, CheckIcon, XIcon, MapPinIcon, StarIcon, PhoneIcon, ClockIcon } from 'lucide-react';
const tabs = [{
  id: 'scrape',
  icon: SearchIcon,
  title: 'Scrape Google Maps at scale',
  description: 'Stop copying data one business at a time. Our Google Maps scraper extracts thousands of local businesses with ratings, reviews, addresses, and phone numbers in minutes — not days.'
}, {
  id: 'enrich',
  icon: MailPlusIcon,
  title: 'Enrich with emails & owner contacts',
  description: "Google Maps doesn't show emails. Our Google Maps email extractor finds verified owner emails, direct phone numbers, and decision-maker names that aren't publicly listed."
}, {
  id: 'export',
  icon: DownloadIcon,
  title: 'Export clean, ready-to-use data',
  description: 'Download enriched local business data as CSV, or push directly to Salesforce, HubSpot, and other CRMs. No cleanup needed — every record is structured, deduplicated, and verified.'
}];
export function ScraperWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-1742">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1743">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1744">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-1745">
            Turn <span className="text-blue-600" data-id="element-1746">Google Maps</span> into your
            lead engine
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-1747">
            What takes hours of manual work, Openmart does in minutes. Extract,
            enrich, and export local business data at scale.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-1748">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-1749">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-1750">
                  <div className="flex items-start gap-6" data-id="element-1751">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-1752">
                      <tab.icon className="w-6 h-6" data-id="element-1753" />
                    </div>
                    <div data-id="element-1754">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-1755">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-1756">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-1757">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-1758">
              <AnimatePresence mode="wait" data-id="element-1759">
                {activeTab === 'scrape' && <motion.div key="scrape" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full" data-id="element-1760">
                    <div className="flex items-center justify-between mb-6 px-2" data-id="element-1761">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-1762">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-1763" />
                        Manual: 40+ hours
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-1764" />
                      <div className="text-xs font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2" data-id="element-1765">
                        <CheckIcon className="w-3.5 h-3.5" data-id="element-1766" />
                        Openmart: 2 minutes
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1767">
                      {/* Before card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm space-y-3" data-id="element-1768">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1769">
                          Before
                        </div>
                        <div className="space-y-2.5" data-id="element-1770">
                          <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-1771">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-1772" />
                            <span data-id="element-1773">Copy business name...</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-1774">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-1775" />
                            <span data-id="element-1776">Copy address...</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-1777">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-1778" />
                            <span data-id="element-1779">Copy phone number...</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-1780">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-1781" />
                            <span data-id="element-1782">No email available</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-1783">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-1784" />
                            <span data-id="element-1785">No owner info</span>
                          </div>
                        </div>
                        <div className="pt-3 border-t border-slate-100 text-center" data-id="element-1786">
                          <span className="text-xs text-red-400 font-semibold" data-id="element-1787">
                            ~3 min per business
                          </span>
                        </div>
                      </div>

                      {/* After card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm border border-blue-100 space-y-3" data-id="element-1788">
                        <div className="flex items-center justify-between mb-1" data-id="element-1789">
                          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-1790">
                            After
                          </div>
                          <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full" data-id="element-1791">
                            1,245 results
                          </span>
                        </div>
                        <div className="space-y-2" data-id="element-1792">
                          {['Brooklyn Roasting Co.', 'Devocion Coffee', 'Sey Coffee'].map((name, i) => <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2" data-id="element-1793">
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-[10px] font-bold flex-shrink-0" data-id="element-1794">
                                {name[0]}
                              </div>
                              <div className="min-w-0" data-id="element-1795">
                                <div className="font-semibold text-slate-900 truncate" data-id="element-1796">
                                  {name}
                                </div>
                                <div className="text-slate-400 flex items-center gap-1.5" data-id="element-1797">
                                  <StarIcon className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" data-id="element-1798" />
                                  4.{7 + i}
                                  <span className="mx-0.5" data-id="element-1799">·</span>
                                  <MapPinIcon className="w-2.5 h-2.5" data-id="element-1800" />
                                  Brooklyn
                                </div>
                              </div>
                            </div>)}
                        </div>
                        <div className="pt-3 border-t border-slate-100 text-center" data-id="element-1801">
                          <span className="text-xs text-green-600 font-semibold" data-id="element-1802">
                            All 1,245 in 2 minutes
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'enrich' && <motion.div key="enrich" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-1803">
                    <div className="flex items-center justify-between mb-6 px-2" data-id="element-1804">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1805">
                        Google Maps data
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-1806" />
                      <div className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-1807">
                        + Openmart enrichment
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1808">
                      {/* Before card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm space-y-4" data-id="element-1809">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1810">
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-sm font-bold" data-id="element-1811">
                            BR
                          </div>
                          <div data-id="element-1812">
                            <div className="text-sm font-bold text-slate-900" data-id="element-1813">
                              Brooklyn Roasting
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-1814">
                              25 Jay St, Brooklyn
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2.5 pt-2 border-t border-slate-100" data-id="element-1815">
                          <div className="flex items-center gap-2 text-xs" data-id="element-1816">
                            <CheckIcon className="w-4 h-4 text-slate-300" data-id="element-1817" />
                            <span className="text-slate-500" data-id="element-1818">
                              (718) 855-1000
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-1819">
                            <XIcon className="w-4 h-4" data-id="element-1820" />
                            <span data-id="element-1821">No email available</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-1822">
                            <XIcon className="w-4 h-4" data-id="element-1823" />
                            <span data-id="element-1824">No owner info</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-1825">
                            <XIcon className="w-4 h-4" data-id="element-1826" />
                            <span data-id="element-1827">No social profiles</span>
                          </div>
                        </div>
                      </div>

                      {/* After card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm border border-blue-100 space-y-4" data-id="element-1828">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1829">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-bold" data-id="element-1830">
                            BR
                          </div>
                          <div data-id="element-1831">
                            <div className="text-sm font-bold text-slate-900" data-id="element-1832">
                              Brooklyn Roasting
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-1833">
                              25 Jay St, Brooklyn
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2.5 pt-2 border-t border-slate-100" data-id="element-1834">
                          <div className="flex items-center gap-2 text-xs" data-id="element-1835">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-1836" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-1837">
                              (718) 855-1000
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-1838">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-1839" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-1840">
                              info@brooklynroasting.com
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-1841">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-1842" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-1843">
                              Jim Munson, Founder
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-1844">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-1845" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-1846">
                              @brooklynroasting
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'export' && <motion.div key="export" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-1847">
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center" data-id="element-1848">
                      <div className="flex justify-center items-center gap-3 mb-8" data-id="element-1849">
                        <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm" data-id="element-1850">
                          <MapPinIcon className="w-7 h-7 text-blue-600" data-id="element-1851" />
                        </div>
                        <div className="flex flex-col items-center" data-id="element-1852">
                          <ArrowRightIcon className="w-5 h-5 text-blue-400 mb-1" data-id="element-1853" />
                          <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-1854">
                            Export
                          </span>
                        </div>
                        <div className="flex gap-2" data-id="element-1855">
                          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm" data-id="element-1856">
                            <span className="font-bold text-sm text-blue-600" data-id="element-1857">
                              CSV
                            </span>
                          </div>
                          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm" data-id="element-1858">
                            <span className="font-bold text-[10px] text-blue-600 leading-tight text-center" data-id="element-1859">
                              Sales
                              <br data-id="element-1860" />
                              force
                            </span>
                          </div>
                          <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center border border-orange-100 shadow-sm" data-id="element-1861">
                            <span className="font-bold text-[10px] text-orange-600 leading-tight text-center" data-id="element-1862">
                              Hub
                              <br data-id="element-1863" />
                              Spot
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-5 text-left border border-slate-100" data-id="element-1864">
                        <div className="flex items-center gap-3 mb-4" data-id="element-1865">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-1866">
                            <CheckIcon className="w-5 h-5 text-green-600" data-id="element-1867" />
                          </div>
                          <div data-id="element-1868">
                            <h4 className="font-bold text-slate-900 text-sm" data-id="element-1869">
                              Export ready
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-1870">
                              brooklyn_coffee_shops.csv
                            </p>
                          </div>
                        </div>
                        <div className="space-y-3" data-id="element-1871">
                          <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-1872">
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-1873">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-1874">
                                1,245
                              </div>
                              <div className="text-slate-500" data-id="element-1875">businesses</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-1876">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-1877">
                                89%
                              </div>
                              <div className="text-slate-500" data-id="element-1878">have emails</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-1879">
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-1880">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-1881">
                                94%
                              </div>
                              <div className="text-slate-500" data-id="element-1882">have phones</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-1883">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-1884">
                                50+
                              </div>
                              <div className="text-slate-500" data-id="element-1885">data fields</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}